import { type Logger } from 'loglevel';
import { type ServiceTokens } from '../../../container';
import {
  type RealtimeEventCbCancel,
  type Realtime,
  RealtimeEventType,
  type RealtimeEvent,
  type RealtimeIdentity,
} from '@doxyme/realtime';
import {
  type RealtimeMessageType,
  type RealtimeMessage,
  RealtimeMemberStatus,
  RealtimeMember,
} from '../../../features/realtime/realtime.types';
import { type AppStore } from '../../../stores/redux';
import { updateRealtimeState } from '../../../features/realtime/realtime.slice';

type GenericRealtimeServiceDependencies = {
  [ServiceTokens.Realtime]: Realtime;
  [ServiceTokens.Logger]: Logger;
  [ServiceTokens.ReduxStore]: AppStore;
};

type SendWithAckOptions = {
  timeout?: number; // Timeout in milliseconds
  retries?: number; // Number of retry attempts
};

export type AckResult = {
  success: boolean;
  attempt?: number;
};

export class CommonRealtimeService {
  protected readonly logger: Logger;

  protected readonly store: AppStore;

  protected readonly realtime: Realtime;

  protected subscriptions: RealtimeEventCbCancel[] = [];

  constructor({
    logger,
    realtime,
    reduxStore,
  }: GenericRealtimeServiceDependencies) {
    this.logger = logger;
    this.realtime = realtime;
    this.store = reduxStore;
  }

  getMember(participantId: string): RealtimeMember | undefined {
    return this.realtime.membersMap[participantId];
  }

  getIdentity(): RealtimeIdentity {
    return this.realtime.identity;
  }

  on<T = unknown>(
    messageType: RealtimeMessageType,
    callback: (message: RealtimeMessage<T>, event: RealtimeEvent) => void,
    options?: { once?: boolean },
  ): RealtimeEventCbCancel {
    const unsubscribe = this.realtime.on(
      [RealtimeEventType.Message],
      (event: RealtimeEvent) => {
        const messageEvent = event.data as RealtimeMessage<T>;
        if (messageEvent.messageType === messageType) {
          callback(messageEvent, event);

          if (options?.once ?? false) {
            unsubscribe();
          }
        }
      },
    );

    return unsubscribe;
  }

  async send(participantId: string, message: unknown): Promise<unknown> {
    const member = this.realtime.membersMap[participantId];

    if (member) {
      return this.realtime.sendMessage(
        member.identity,
        message as RealtimeMessage,
      );
    }
    this.logger.warn('participant does not exist', participantId);

    return undefined;
  }

  async sendWithAck(
    participantId: string,
    message: RealtimeMessage,
    options: SendWithAckOptions = {},
  ): Promise<AckResult> {
    let unsubscribe: null | (() => void) = null;
    const { timeout = 5000, retries = 3 } = options;
    message.requestAck = true;
    const attemptSend = async (attempt: number): Promise<AckResult> => {
      return new Promise((resolve, reject): void => {
        const handleRetry = (): void => {
          if (attempt < retries) {
            attemptSend(attempt + 1)
              .then(resolve)
              .catch(reject);
          } else {
            reject(
              new Error(
                `Message acknowledgment failed after ${attempt} attempts`,
              ),
            );
          }
        };

        const timeoutId = setTimeout((): void => {
          cleanup();
          handleRetry();
        }, timeout);

        const cleanup = (): void => {
          clearTimeout(timeoutId);
        };

        const handleAck = (event: RealtimeEvent): void => {
          if (this.isMatchingAck(event, message)) {
            cleanup();
            unsubscribe?.();
            resolve({ success: true, attempt });
          }
        };

        unsubscribe = this.realtime.on(
          [RealtimeEventType.MessageAck],
          handleAck,
        );

        this.send(participantId, message).catch((_error) => {
          cleanup();
          handleRetry();
        });
      });
    };

    return attemptSend(1);
  }

  private isMatchingAck(_event: RealtimeEvent, _message: unknown): boolean {
    // TODO: Should be implemented at realtime level.
    return true; // Placeholder implementation
  }

  setStatusToBusy(): void {
    void this.store.dispatch(
      updateRealtimeState({
        status: RealtimeMemberStatus.Busy,
        videoStartedAt: Date.now(),
      }),
    );
  }

  setStatusToBusyOnResume(): void {
    void this.store.dispatch(
      updateRealtimeState({
        status: RealtimeMemberStatus.Busy,
      }),
    );
  }

  setStatusToAvailable(): void {
    void this.store.dispatch(
      updateRealtimeState({
        status: RealtimeMemberStatus.Online,
      }),
    );
  }

  setStatusToOnHold(data: {
    sessionId: string;
    dbSessionId?: string;
    sessionOwnershipToken: string;
  }): void {
    void this.store.dispatch(
      updateRealtimeState({
        status: RealtimeMemberStatus.OnHold,
        ...data,
      }),
    );
  }

  dispose(): void {
    for (const unsubscribe of this.subscriptions) {
      unsubscribe();
    }
  }
}
